import { useMutation, useQuery, useQueryClient } from 'react-query';
import type { AxiosResponse } from 'axios';

import type {
  CreateTravelerDto,
  PagedResponse,
  TravelerDocumentMatchDto,
  TravelerResponseDto,
  UpdateTravelerDto,
} from '@bandido/shared/dtos';

import { client } from '@frontend/features/common/services';
import { QueryKeys } from '@frontend/features/common/types';

import {
  isLookupDocumentNumber,
  normalizeDocumentNumber,
} from './travelerDocumentLookup';
import type {
  LinkCaseTravelerByDocumentVariables,
  TravelerListParams,
  UpdateCaseTravelerVariables,
} from './types';

const travelerQueryParams = ({
  search = '',
  currentPage = 1,
  pageSize = 10,
}: TravelerListParams) => ({ search, currentPage, pageSize });

export const caseTravelersQueryKey = (params: TravelerListParams) => [
  QueryKeys.getCaseTravelers,
  params.caseId,
  travelerQueryParams(params),
];

export const caseTravelerSuggestionsQueryKey = (
  params: TravelerListParams,
) => [
  QueryKeys.getCaseTravelerSuggestions,
  params.caseId,
  travelerQueryParams(params),
];

export const useCaseTravelers = (params: TravelerListParams) =>
  useQuery({
    queryKey: caseTravelersQueryKey(params),
    queryFn: async () => {
      const response: AxiosResponse<PagedResponse<TravelerResponseDto>> =
        await client.get(`/case/${params.caseId}/travelers`, {
          params: travelerQueryParams(params),
        });
      return response.data;
    },
    enabled: params.enabled !== false && Boolean(params.caseId),
    keepPreviousData: true,
  });

const ALL_CASE_TRAVELERS_PAGE_SIZE = 100;

export const fetchAllCaseTravelers = async (
  caseId: number,
): Promise<PagedResponse<TravelerResponseDto>> => {
  const first = (
    await client.get<PagedResponse<TravelerResponseDto>>(
      `/case/${caseId}/travelers`,
      { params: { search: '', currentPage: 1, pageSize: ALL_CASE_TRAVELERS_PAGE_SIZE } },
    )
  ).data;
  const pages = Math.ceil(first.count / ALL_CASE_TRAVELERS_PAGE_SIZE);
  const remaining = await Promise.all(
    Array.from({ length: Math.max(0, pages - 1) }, (_, index) =>
      client.get<PagedResponse<TravelerResponseDto>>(
        `/case/${caseId}/travelers`,
        {
          params: {
            search: '',
            currentPage: index + 2,
            pageSize: ALL_CASE_TRAVELERS_PAGE_SIZE,
          },
        },
      )),
  );
  return {
    items: [first, ...remaining.map((response) => response.data)]
      .flatMap((page) => page.items),
    count: first.count,
    pages: 1,
    currentPage: 1,
  };
};

/** El booking necesita poder elegir cualquier integrante, no sólo una página. */
export const useAllCaseTravelers = (caseId: number, enabled = true) =>
  useQuery({
    queryKey: [QueryKeys.getCaseTravelers, caseId, 'all'],
    queryFn: () => fetchAllCaseTravelers(caseId),
    enabled: enabled && Boolean(caseId),
  });

export const useCaseTravelerSuggestions = (params: TravelerListParams) =>
  useQuery({
    queryKey: caseTravelerSuggestionsQueryKey(params),
    queryFn: async () => {
      const response: AxiosResponse<PagedResponse<TravelerResponseDto>> =
        await client.get(`/case/${params.caseId}/travelers/suggestions`, {
          params: travelerQueryParams(params),
        });
      return response.data;
    },
    enabled: params.enabled !== false && Boolean(params.caseId),
    keepPreviousData: true,
  });

export const travelerDocumentMatchesQueryKey = (
  caseId: number,
  documentNumber: string,
) => [
  QueryKeys.getTravelerDocumentMatches,
  caseId,
  normalizeDocumentNumber(documentNumber),
];

/**
 * Fichas de toda la agencia con ese número exacto de documento. Es una
 * lectura, pero el número va en el body: nunca en la URL.
 */
export const useTravelerDocumentMatches = (
  caseId: number,
  documentNumber: string,
  enabled = true,
) =>
  useQuery({
    queryKey: travelerDocumentMatchesQueryKey(caseId, documentNumber),
    queryFn: async () => {
      const response: AxiosResponse<TravelerDocumentMatchDto[]> =
        await client.post(`/case/${caseId}/travelers/document-matches`, {
          documentNumber,
        });
      return response.data;
    },
    enabled:
      enabled && Boolean(caseId) && isLookupDocumentNumber(documentNumber),
    staleTime: 30_000,
  });

const useInvalidateCaseTravelers = (caseId: number) => {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      queryClient.invalidateQueries([QueryKeys.getCaseTravelers, caseId]),
      queryClient.invalidateQueries([
        QueryKeys.getCaseTravelerSuggestions,
        caseId,
      ]),
      // Un alta, una edición o un vínculo cambian qué fichas tienen un
      // documento y su relación con el caso.
      queryClient.invalidateQueries([
        QueryKeys.getTravelerDocumentMatches,
        caseId,
      ]),
    ]);
};

export const useCreateCaseTraveler = (caseId: number) => {
  const invalidate = useInvalidateCaseTravelers(caseId);
  return useMutation({
    mutationFn: async (input: CreateTravelerDto) =>
      (await client.post<TravelerResponseDto>(`/case/${caseId}/travelers`, input))
        .data,
    onSuccess: invalidate,
  });
};

export const useUpdateCaseTraveler = (caseId: number) => {
  const invalidate = useInvalidateCaseTravelers(caseId);
  return useMutation({
    mutationFn: async ({
      travelerId,
      input,
    }: UpdateCaseTravelerVariables) =>
      (
        await client.patch<TravelerResponseDto>(
          `/case/${caseId}/travelers/${travelerId}`,
          input,
        )
      ).data,
    onSuccess: invalidate,
  });
};

export const useAssociateCaseTraveler = (caseId: number) => {
  const invalidate = useInvalidateCaseTravelers(caseId);
  return useMutation({
    mutationFn: async (travelerId: string) =>
      (
        await client.post<TravelerResponseDto>(
          `/case/${caseId}/travelers/${travelerId}`,
        )
      ).data,
    onSuccess: invalidate,
  });
};

/** Usa en el caso una ficha de la agencia presentando uno de sus documentos. */
export const useLinkCaseTravelerByDocument = (caseId: number) => {
  const invalidate = useInvalidateCaseTravelers(caseId);
  return useMutation({
    mutationFn: async ({
      travelerId,
      documentNumber,
      replacesTravelerId,
    }: LinkCaseTravelerByDocumentVariables) =>
      (
        await client.post<TravelerResponseDto>(
          `/case/${caseId}/travelers/${travelerId}/link-by-document`,
          {
            documentNumber,
            ...(replacesTravelerId ? { replacesTravelerId } : {}),
          },
        )
      ).data,
    onSuccess: invalidate,
  });
};

export const useRemoveCaseTraveler = (caseId: number) => {
  const invalidate = useInvalidateCaseTravelers(caseId);
  return useMutation({
    mutationFn: (travelerId: string) =>
      client.delete(`/case/${caseId}/travelers/${travelerId}`),
    onSuccess: invalidate,
  });
};

export type { UpdateTravelerDto };
